import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Info, UserCog } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";
import { CATEGORY_LABELS } from "./erLabels";

export default function ErIntakeForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [category, setCategory] = useState(sp.get("category") || "");
  const [description, setDescription] = useState("");
  const [parties, setParties] = useState("");
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState(user?.campus_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/campuses").then((r) => setCampuses(r.data)).catch(() => setCampuses([]));
  }, []);

  const canSubmit = category && (description || "").trim().length > 10 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/er/intake", {
        reporter_type: "identified",
        category,
        description: description.trim(),
        parties_involved: parties.trim() || null,
        campus_id: campusId || null,
      });
      toast.success(`Report submitted — ${data.case_number}. People Operations will be in touch.`);
      navigate(`/er/my-reports/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="er-intake-page">
      <button
        onClick={() => navigate("/er")}
        className="inline-flex items-center text-forest-700 text-sm font-medium"
        data-testid="er-intake-back"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back to ER Center
      </button>

      <div>
        <ModuleTitleER />
        <h1 className="font-serif text-forest-900 text-4xl mt-3">Raise A Concern</h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl">
          You are signing this report as{" "}
          <span className="font-medium text-forest-900">{user?.name}</span>. People Operations
          will handle this with care and confidentiality.
        </p>
      </div>

      <section className="patria-card p-6 border-l-4 border-terracotta-500 flex items-start gap-3">
        <Info className="h-5 w-5 text-terracotta-500 mt-0.5 shrink-0" strokeWidth={1.5} />
        <p className="text-sm text-charcoal-soft">
          Prefer not to share your identity?{" "}
          <button
            onClick={() => navigate("/er/anonymous")}
            className="patria-link"
            data-testid="er-intake-switch-anon"
          >
            Submit anonymously instead
          </button>
          . You&apos;ll receive a case number and a token to check on the report later — no email or
          identity is collected.
        </p>
      </section>

      <section className="patria-card p-6 space-y-5" data-testid="er-intake-form">
        <div>
          <label className="patria-label" htmlFor="category">Category *</label>
          <select
            id="category"
            data-testid="er-intake-category"
            className="patria-input mt-2"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Choose a category…</option>
            {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="patria-label" htmlFor="desc">What happened? *</label>
          <textarea
            id="desc"
            data-testid="er-intake-description"
            className="patria-input mt-2"
            rows={7}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Share what happened, when, and how it affected you. Take your time — every detail matters."
          />
          <p className="mt-1 text-xs text-charcoal-muted">Minimum 10 characters.</p>
        </div>

        <div>
          <label className="patria-label" htmlFor="parties">Parties involved (optional)</label>
          <textarea
            id="parties"
            data-testid="er-intake-parties"
            className="patria-input mt-2"
            rows={2}
            value={parties}
            onChange={(e) => setParties(e.target.value)}
            placeholder="Names or roles of anyone involved (optional)."
          />
        </div>

        <div>
          <label className="patria-label" htmlFor="campus">Campus (optional)</label>
          <select
            id="campus"
            data-testid="er-intake-campus"
            className="patria-input mt-2"
            value={campusId}
            onChange={(e) => setCampusId(e.target.value)}
          >
            <option value="">— unspecified —</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="pt-4 flex flex-wrap gap-3 justify-end border-t border-sage-300/60">
          <button
            className="patria-btn-secondary"
            onClick={() => navigate("/er")}
            disabled={saving}
            data-testid="er-intake-cancel"
          >
            Cancel
          </button>
          <button
            className="patria-btn-primary"
            disabled={!canSubmit}
            onClick={submit}
            data-testid="er-intake-submit"
          >
            {saving ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </section>

      <p className="text-xs text-charcoal-muted flex items-start gap-2 max-w-2xl">
        <UserCog className="h-3.5 w-3.5 mt-0.5" strokeWidth={1.5} />
        <span>
          Your report is stored securely. People Operations will acknowledge it within 2
          business days and follow up via MyPatria.
        </span>
      </p>
    </div>
  );
}
